import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { CheckCircle, Shield, Leaf, Award, FileCheck, Users } from "lucide-react";
import fscBadge from "@/assets/fsc-badge.jpg";

const certifications = [
  {
    id: "fsc",
    name: "FSC® (Forest Stewardship Council)",
    description: "La certification FSC garantit que nos produits proviennent de forêts gérées de manière responsable. Elle assure une gestion forestière respectueuse de l'environnement, socialement bénéfique et économiquement viable.",
    image: fscBadge,
    benefits: [
      "Préservation de la biodiversité",
      "Respect des droits des travailleurs forestiers",
      "Gestion durable des ressources",
      "Traçabilité de la chaîne d'approvisionnement",
    ],
  },
  {
    id: "iso",
    name: "ISO 9001:2015",
    description: "Cette norme internationale de management de la qualité atteste de notre engagement envers l'amélioration continue de nos processus. Elle garantit une organisation orientée vers la satisfaction client.",
    icon: Award,
    benefits: [
      "Processus de production optimisés",
      "Amélioration continue de la qualité",
      "Satisfaction client au cœur des priorités",
      "Réduction des non-conformités",
    ],
  },
  {
    id: "food",
    name: "Normes européennes d'emballage alimentaire",
    description: "Nos emballages sont conformes aux réglementations européennes (CE) n°1935/2004 et n°10/2011 concernant les matériaux destinés à entrer en contact avec les denrées alimentaires.",
    icon: Shield,
    benefits: [
      "Sécurité alimentaire garantie",
      "Absence de migration de substances nocives",
      "Tests de conformité réguliers",
      "Documentation complète disponible",
    ],
  },
];

const controlSteps = [
  {
    step: "1",
    title: "Contrôle à réception",
    description: "Vérification des matières premières dès leur arrivée pour garantir leur conformité.",
    icon: FileCheck,
  },
  {
    step: "2",
    title: "Contrôle en production",
    description: "Suivi continu pendant la fabrication pour détecter toute anomalie.",
    icon: Shield,
  },
  {
    step: "3",
    title: "Contrôle final",
    description: "Inspection complète avant expédition par un organisme certificateur indépendant.",
    icon: CheckCircle,
  },
];

const CertificationCard = ({ cert, index }: { cert: typeof certifications[0]; index: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.15 }}
      className="bg-card rounded-2xl border border-border overflow-hidden hover:border-primary/20 transition-all duration-500 hover-float"
    >
      {/* Header */}
      <div className="p-8 border-b border-border">
        <div className="flex items-start gap-6">
          <div className="flex-shrink-0">
            {cert.image ? (
              <img
                src={cert.image}
                alt={cert.name}
                className="w-20 h-20 object-contain rounded-xl"
              />
            ) : (
              <div className="w-20 h-20 rounded-xl bg-primary/10 flex items-center justify-center">
                {cert.icon && <cert.icon className="w-10 h-10 text-primary" />}
              </div>
            )}
          </div>
          <div>
            <h3 className="font-display text-xl font-bold text-foreground mb-2">
              {cert.name}
            </h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {cert.description}
            </p>
          </div>
        </div>
      </div>

      {/* Benefits */}
      <div className="p-8 bg-secondary/30">
        <h4 className="font-semibold text-foreground mb-4">Avantages :</h4>
        <ul className="grid sm:grid-cols-2 gap-3">
          {cert.benefits.map((benefit, i) => (
            <li key={i} className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-eco flex-shrink-0 mt-1" />
              <span className="text-sm text-foreground">{benefit}</span>
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
};

const Certifications = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-b from-secondary/50 to-background relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-primary/10 blur-3xl" />
            <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-eco/10 blur-3xl" />
          </div>

          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium mb-6">
                <Award className="w-4 h-4" />
                Garanties qualité
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                Nos Certifications
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Nos certifications témoignent de notre engagement envers la qualité, 
                la sécurité alimentaire et le respect de l'environnement.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Certifications */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="space-y-8">
              {certifications.map((cert, index) => (
                <CertificationCard key={cert.id} cert={cert} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Control Process */}
        <section className="py-20 bg-secondary/30">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-2xl mx-auto mb-16"
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">
                Notre processus de contrôle qualité
              </h2>
              <p className="text-muted-foreground">
                Chaque produit est soumis à un contrôle rigoureux à chaque étape, 
                effectué par un organisme certificateur indépendant.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {controlSteps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative text-center"
                >
                  <div className="w-20 h-20 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-6 text-2xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-3">
                    {step.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {step.description}
                  </p>

                  {/* Connector Line */}
                  {index < controlSteps.length - 1 && (
                    <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-0.5 bg-border" />
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <Users className="w-16 h-16 text-primary mx-auto mb-6" />
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-6">
                La confiance de nos clients
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Depuis notre création, nous avons accompagné des centaines de professionnels 
                de la restauration dans leur transition vers des emballages durables. 
                Notre rigueur et notre engagement qualité font de nous un partenaire de confiance.
              </p>
              <div className="flex flex-wrap justify-center gap-8">
                <div className="text-center">
                  <p className="font-display text-4xl font-bold text-primary">500+</p>
                  <p className="text-sm text-muted-foreground">Clients satisfaits</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-4xl font-bold text-primary">100%</p>
                  <p className="text-sm text-muted-foreground">Produits certifiés</p>
                </div>
                <div className="text-center">
                  <p className="font-display text-4xl font-bold text-primary">10+</p>
                  <p className="text-sm text-muted-foreground">Années d'expérience</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Certifications;